import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Github, Mail, Facebook, Instagram } from 'lucide-react';

const TeamSection = styled.section`
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
`;

const TeamContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00ff41, #00ffff, #ff0080);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 80px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 60px;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const TeamCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 40px 30px;
  border-radius: 25px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(135,206,235,0.1), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(135,206,235,0.6);
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    
    &::before {
      left: 100%;
    }
  }
`;

const TeamPhoto = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(135,206,235,0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(255,255,255,0.1), transparent);
  }
`;

const TeamName = styled.h3`
  font-size: 1.4rem;
  color: #87ceeb;
  margin-bottom: 10px;
  font-weight: 700;
`;

const TeamRole = styled.div`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 15px;
`;

const TeamDescription = styled.p`
  color: rgba(255,255,255,0.9);
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 25px;
`;

const TeamSkills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 25px;
`;

const Skill = styled.span`
  background: rgba(135,206,235,0.2);
  color: #87ceeb;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(135,206,235,0.3);
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(135,206,235,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #87ceeb;
  transition: all 0.3s ease;
  text-decoration: none;
  border: 1px solid rgba(135,206,235,0.3);
  
  &:hover {
    background: #87ceeb;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(135,206,235,0.4);
  }
`;

const ProfessionalTeam = () => {
const teamMembers = [
    // Shivam Singh card - Hidden
    // {
    //   name: 'Shivam Singh',
    //   role: 'Founder & Lead Developer',
    //   description: '3+ years of experience in full-stack development. B.Tech Computer Science. Expert in Full-Stack Development, UI/UX Design, and MERN Stack.',
    //   skills: ['Full-Stack Development', 'UI/UX Design', 'MERN Stack', 'React', 'Node.js'],
    //   initials: 'SS'
    // },
    // Vijay Kumar card - Hidden
    // {
    //   name: 'Vijay Kumar',
    //   role: 'Tech Lead & Full Stack Developer',
    //   description: 'Passionate full stack developer skilled in building modern web applications with clean code and great user experiences. Proficient in MERN stack and cloud technologies.',
    //   skills: ['React.js', 'Node.js', 'MongoDB', 'JavaScript', 'REST APIs', 'IT Support', 'Networking'],
    //   initials: 'VK',
    //   github: '',
    //   portfolio: 'https://vijayxtech.github.io/',
    //   linkedin: '',
    //   twitter: '',
    //   facebook: '',
    //   instagram: ''
    // },

    {
      name: 'Satyam Kumar',
      role: 'Founder ',
      description: 'Visionary civil engineering leader with expertise in architectural design, structural planning, and infrastructure development.',
      skills: ['Civil Engineering', 'Architectural Design', 'Structural Planning', 'AutoCAD', 'Project Management'],
      initials: 'SK'
    },

    {
      name: 'Shweta Singh',
      role: 'Architect Designer',
      description: 'Creative architectural designer with expertise in residential and commercial design, delivering innovative and functional spaces through modern design principles.',
      skills: ['AutoCAD', 'SketchUp', '3D Modeling', 'Interior Design', 'Structural Planning'],
      initials: 'SS'
    },

    {
      name: 'Chitranjan Kumar',
      role: 'Frontend Developer',
      description: 'Skilled frontend developer passionate about building responsive, visually appealing, and user-friendly web interfaces using modern technologies.',
      skills: ['React.js', 'HTML', 'CSS', 'JavaScript', 'Tailwind CSS'],
      initials: 'CK'
    },
    
    {
      name: 'Pratyush Shrivastav',
      role: 'Marketing Manager',
      description: 'Responsible for planning and executing all marketing activities at InitX Technologies, including social media management, promotional campaigns, client outreach, and brand awareness to drive business growth.',
      skills: ['Social Media Management', 'Campaign Planning', 'Client Outreach', 'Brand Awareness', 'Content Creation'],
      initials: 'PS'
    },
    {
      name: 'Shivani Kumari',
      role: 'Human Resources',
      description: 'Manages all HR operations at InitX Technologies including recruitment, onboarding, employee engagement, and maintaining a positive work culture to support team growth and well-being.',
      skills: ['Recruitment', 'Onboarding', 'Employee Engagement', 'HR Operations', 'Team Management'],
      initials: 'SK'
    },
    {
      name: 'Aman Raushan',
      role: 'QA Tester',
      description: 'Dedicated QA professional responsible for ensuring the quality, reliability, and performance of all software products at InitX Technologies. Specializes in manual and automated testing to deliver bug-free, high-quality releases.',
      skills: ['Manual Testing', 'Automated Testing', 'Bug Tracking', 'Selenium', 'Test Case Design', 'Regression Testing', 'Postman'],
      initials: 'AR'
    },
    {
      name: 'Yash Kumar Om',
      role: 'Client Relationship Manager',
      description: 'Dedicated Client Relationship Manager at InitX Technologies responsible for building and maintaining strong client partnerships. Ensures client satisfaction, manages communications, and acts as the primary point of contact between clients and the technical team to deliver seamless project experiences.',
      skills: ['Client Management', 'Relationship Building', 'Communication', 'Problem Solving', 'Project Coordination', 'Client Onboarding', 'Feedback Management'],
      initials: 'YO'
    }
  ];

  return (
    <TeamSection>
      <TeamContainer>
        <SectionTitle>Meet Our Expert Team</SectionTitle>
        <SectionSubtitle>
          Talented professionals dedicated to delivering exceptional technology solutions and driving your success
        </SectionSubtitle>
        
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <TeamPhoto>
                {member.initials}
              </TeamPhoto>
              <TeamName>{member.name}</TeamName>
              <TeamRole>{member.role}</TeamRole>
              <TeamDescription>{member.description}</TeamDescription>
              <TeamSkills>
                {member.skills.map((skill, idx) => (
                  <Skill key={idx}>{skill}</Skill>
                ))}
              </TeamSkills>
              <SocialLinks>
                <SocialLink href={member.linkedin || '#'} aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                  <Linkedin size={18} />
                </SocialLink>
                <SocialLink href={member.twitter || '#'} aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                  <Twitter size={18} />
                </SocialLink>
                <SocialLink href={member.github || '#'} aria-label="GitHub" target="_blank" rel="noopener noreferrer">
                  <Github size={18} />
                </SocialLink>
                {member.facebook && (
                  <SocialLink href={member.facebook} aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <Facebook size={18} />
                  </SocialLink>
                )}
                {member.instagram && (
                  <SocialLink href={member.instagram} aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <Instagram size={18} />
                  </SocialLink>
                )}
                <SocialLink href={member.portfolio ? member.portfolio : `mailto:#`} aria-label="Portfolio/Email" target="_blank" rel="noopener noreferrer">
                  <Mail size={18} />
                </SocialLink>
              </SocialLinks>
            </TeamCard>
          ))}
        </TeamGrid>
      </TeamContainer>
    </TeamSection>
  );
};

export default ProfessionalTeam;